import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * Export gate modal for Quick Reports.
 * - requireAll=true (Payroll Projection): all in-scope employees must be processed
 * - requireAll=false (Register / Reco / Journal): at least one processed
 */
export default function PayrollAllProcessedGateModal({
  darkMode,
  open,
  onClose,
  reportLabel = "this report",
  gate = null,
}) {
  if (!open) return null;

  const requireAll = gate?.requireAll !== false;
  const shell = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const btn = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:border-[hsl(var(--ds-primary))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:border-[hsl(var(--ds-primary))]";

  const processed = gate?.processedCount ?? 0;
  const scope = gate?.scopeCount ?? 0;
  const unprocessed = gate?.unprocessedCount ?? Math.max(0, scope - processed);

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
      <div className={`w-full max-w-md rounded-lg border shadow-xl ${shell}`}>
        <div
          className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 shrink-0 text-amber-500" size={18} />
            <div>
              <h3 className="text-sm font-semibold">
                {requireAll ? "Process all employees first" : "Process at least one employee"}
              </h3>
              <p className={`mt-0.5 text-[11px] ${muted}`}>
                Required before exporting {reportLabel}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`rounded p-1 ${btn}`}>
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3 px-4 py-4 text-xs">
          <p>
            {requireAll
              ? "Please process all employees' payroll before exporting this report."
              : "Process at least one employee before exporting this report. Only processed employees are included."}
          </p>
          <div
            className={`rounded border px-3 py-2 tabular-nums ${
              darkMode ? "border-amber-800/40 bg-amber-950/30" : "border-amber-200 bg-amber-50"
            }`}
          >
            <div>
              Processed: <strong>{processed}</strong> / <strong>{scope}</strong>
            </div>
            {requireAll ? (
              <div>
                Still unprocessed: <strong>{unprocessed}</strong>
              </div>
            ) : null}
          </div>
          {gate?.message ? <p className={muted}>{gate.message}</p> : null}
        </div>

        <div
          className={`flex justify-end border-t px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
